import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users, UserCheck, UserPlus, Building2, ClipboardList, Briefcase, FileText, CalendarDays, Inbox, GraduationCap, Award, Wallet } from 'lucide-react';
import { employeeApi } from '../../api/hr';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';

/**
 * Real KPIs, computed from actual data - no illustrative numbers.
 * Now also the real navigation hub for every HR section, replacing
 * the 9-item tab strip per explicit feedback that it was too many
 * tabs to navigate - same icon-tile pattern already used on the
 * Staff Dashboard.
 */
export function HRDashboardPage() {
  const employeesQuery = useQuery({ queryKey: ['hr-employees'], queryFn: () => employeeApi.listForHR() });
  const employees = employeesQuery.data?.data ?? [];

  const totalStaff = employees.length;
  const activeStaff = employees.filter((e) => e.employment_status === 'active').length;
  const onProbation = employees.filter((e) => e.employment_status === 'probation').length;
  const withoutStaffId = employees.filter((e) => !e.staff_id).length;

  const deptCounts = new Map<string, number>();
  employees.forEach((e) => {
    const name = e.department_name ?? 'Unassigned';
    deptCounts.set(name, (deptCounts.get(name) ?? 0) + 1);
  });

  return (
    <div className="page">
      <PageHeader title="HR Dashboard" />

      <div className="kpi-grid">
        <KpiCard icon={<Users size={20} />} color="#eff6ff" iconColor="#1d4ed8" label="Total Staff" value={totalStaff} loading={employeesQuery.isLoading} />
        <KpiCard icon={<UserCheck size={20} />} color="#f0fdf4" iconColor="#15803d" label="Active Staff" value={activeStaff} loading={employeesQuery.isLoading} />
        <KpiCard icon={<UserPlus size={20} />} color="#fffbeb" iconColor="#b45309" label="On Probation" value={onProbation} loading={employeesQuery.isLoading} />
        <KpiCard icon={<Building2 size={20} />} color="#f5f3ff" iconColor="#7c3aed" label="Incomplete Staff Files" value={withoutStaffId} loading={employeesQuery.isLoading} />
      </div>

      <div className="quick-actions-grid">
        <Link className="quick-action" to="/hr/staff">
          <span className="quick-action-icon"><Users size={18} /></span>
          <div><strong>Staff Management</strong><div className="muted">View, edit, or add staff</div></div>
        </Link>
        <Link className="quick-action" to="/hr/workforce-requests">
          <span className="quick-action-icon"><ClipboardList size={18} /></span>
          <div><strong>Workforce Requests</strong><div className="muted">Position requests awaiting approval</div></div>
        </Link>
        <Link className="quick-action" to="/hr/vacancies">
          <span className="quick-action-icon"><Briefcase size={18} /></span>
          <div><strong>Recruitment</strong><div className="muted">Vacancies and applicants</div></div>
        </Link>
        <Link className="quick-action" to="/hr/contract-templates">
          <span className="quick-action-icon"><FileText size={18} /></span>
          <div><strong>Contract Templates</strong><div className="muted">Manage contract templates</div></div>
        </Link>
        <Link className="quick-action" to="/hr/leave-review">
          <span className="quick-action-icon"><CalendarDays size={18} /></span>
          <div><strong>Leave Review</strong><div className="muted">Confirm approved leave</div></div>
        </Link>
        <Link className="quick-action" to="/hr/requests">
          <span className="quick-action-icon"><Inbox size={18} /></span>
          <div><strong>HR Requests</strong><div className="muted">Review staff requests</div></div>
        </Link>
        <Link className="quick-action" to="/hr/training-review">
          <span className="quick-action-icon"><GraduationCap size={18} /></span>
          <div><strong>Training Review</strong><div className="muted">Approve and complete training</div></div>
        </Link>
        <Link className="quick-action" to="/hr/performance">
          <span className="quick-action-icon"><Award size={18} /></span>
          <div><strong>Performance</strong><div className="muted">Cycles and appraisals</div></div>
        </Link>
        <Link className="quick-action" to="/hr/payroll">
          <span className="quick-action-icon"><Wallet size={18} /></span>
          <div><strong>Payroll &amp; Timesheets</strong><div className="muted">Generate and manage monthly payroll</div></div>
        </Link>
        <Link className="quick-action" to="/hr/onboarding">
          <span className="quick-action-icon"><ClipboardList size={18} /></span>
          <div><strong>Onboarding</strong><div className="muted">Monitor new hire progress</div></div>
        </Link>
        <Link className="quick-action" to="/hr/probation">
          <span className="quick-action-icon"><UserCheck size={18} /></span>
          <div><strong>Probation &amp; Confirmation</strong><div className="muted">Track reviews and confirmations</div></div>
        </Link>
        <Link className="quick-action" to="/hr/staff-movement">
          <span className="quick-action-icon"><Briefcase size={18} /></span>
          <div><strong>Staff Movement</strong><div className="muted">Promotions, transfers, and grade changes</div></div>
        </Link>
        <Link className="quick-action" to="/hr/restricted-cases">
          <span className="quick-action-icon"><FileText size={18} /></span>
          <div><strong>Restricted Cases</strong><div className="muted">Confidential - authorised personnel only</div></div>
        </Link>
        <Link className="quick-action" to="/hr/separation">
          <span className="quick-action-icon"><UserPlus size={18} /></span>
          <div><strong>Separation &amp; Exit</strong><div className="muted">Resignations, terminations, and clearances</div></div>
        </Link>
      </div>

      <div className="panel">
        <SectionHeader title="Staff by Department" />
        <div className="panel-body">
        {deptCounts.size === 0 ? (
          <p className="muted">No staff records yet.</p>
        ) : (
          <ul className="legend-list">
            {Array.from(deptCounts.entries()).map(([name, count]) => (
              <li key={name}>{name} - {count}</li>
            ))}
          </ul>
        )}
      </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, color, iconColor, label, value, loading }: { icon: React.ReactNode; color: string; iconColor: string; label: string; value: number; loading: boolean }) {
  return (
    <div className="kpi-card">
      <div className="kpi-icon" style={{ background: color, color: iconColor }}>{icon}</div>
      <div>
        <div className="kpi-value">{loading ? '…' : value}</div>
        <div className="kpi-label">{label}</div>
      </div>
    </div>
  );
}
